import { getComputedTunnelStatus } from "../../WireGuardTunnels/wireGuardTunnelStatusUtils";
import { WireGuardTunnel } from "../troubleshootingInterfaces";

export type PeerHealth = "healthy" | "stale" | "never";

/**
 * Health of one WireGuard peer from what `wireguard_tunnel/` already returns.
 *
 * Reuses the 15-minute rule from the WireGuard page so "Up" there and "Healthy"
 * here agree. "never" is a peer that has not completed a handshake at all —
 * usually a tunnel that was created but the customer has not brought up yet,
 * which is a different conversation from one that used to work.
 */
export const getPeerHealth = (
  tunnel: WireGuardTunnel,
  nowMs = Date.now()
): PeerHealth => {
  if (!tunnel.last_handshake) return "never";
  return getComputedTunnelStatus(tunnel.last_handshake, nowMs) === "Up"
    ? "healthy"
    : "stale";
};

export const PEER_HEALTH_LABEL: Record<PeerHealth, string> = {
  healthy: "Healthy",
  stale: "Stale handshake",
  never: "Never handshaked",
};

/** Stale peers grouped by gateway server, so one gateway with several dropped peers reads as one finding. */
export const groupStalePeersByServer = (
  tunnels: WireGuardTunnel[],
  nowMs = Date.now()
): Map<number | null, WireGuardTunnel[]> => {
  const groups = new Map<number | null, WireGuardTunnel[]>();
  tunnels.forEach((tunnel) => {
    if (getPeerHealth(tunnel, nowMs) !== "stale") return;
    const list = groups.get(tunnel.server) || [];
    list.push(tunnel);
    groups.set(tunnel.server, list);
  });
  return groups;
};

/**
 * The Peer health header line. Counts peers that stopped handshaking and
 * peers that never did; only says "All N peers handshaking." when both are 0.
 */
export const peerHealthSummary = (
  tunnels: WireGuardTunnel[],
  nowMs = Date.now()
): { text: string; problem: boolean } => {
  const total = tunnels.length;
  const count = (health: PeerHealth) =>
    tunnels.filter((tunnel) => getPeerHealth(tunnel, nowMs) === health).length;
  const stale = count("stale");
  const never = count("never");
  if (stale === 0 && never === 0)
    return { text: `All ${total} peers handshaking.`, problem: false };
  const neverText = `${never} never handshaked`;
  if (stale === 0)
    return {
      text: `${never} of ${total} peers never handshaked.`,
      problem: true,
    };
  return {
    text: `${stale} of ${total} peers stale${
      never > 0 ? `, ${neverText}` : ""
    }.`,
    problem: true,
  };
};
